'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Heart, Download, Eye, Upload, Settings, Grid3X3, Bookmark, ExternalLink, Star, LogIn, Camera, Link2, Trash2, Crown } from 'lucide-react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import ClipCard from '@/components/ClipCard';
import { useClips } from '@/lib/useClips';
import { useAuth } from '@/lib/AuthContext';

import { deleteClip, getSavedClips, onSavedClipsUpdated } from '@/lib/clipStore';

function formatCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toString();
}

const SOCIAL_PLATFORMS = [
  { key: 'twitter', label: 'Twitter / X', icon: Link2, placeholder: 'https://twitter.com/yourhandle', color: '#1DA1F2' },
  { key: 'instagram', label: 'Instagram', icon: Link2, placeholder: 'https://instagram.com/yourhandle', color: '#E1306C' },
  { key: 'youtube', label: 'YouTube', icon: Link2, placeholder: 'https://youtube.com/@yourchannel', color: '#FF0000' },
  { key: 'tiktok', label: 'TikTok', icon: Link2, placeholder: 'https://tiktok.com/@yourhandle', color: '#010101' },
  { key: 'twitch', label: 'Twitch', icon: Link2, placeholder: 'https://twitch.tv/yourchannel', color: '#9146FF' },
] as const;

export default function ProfileContent() {
  const { user, isLoading: authLoading, updateProfile, isOwner } = useAuth();
  const [activeTab, setActiveTab] = useState<'clips' | 'saved'>('clips');
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [savedClips, setSavedClips] = useState<import('@/components/ClipCard').ClipData[]>([]);

  const { clips: profileClips, loading: loadingClips, isEmpty: emptyClips } = useClips({
    creatorUsername: user?.profile?.username ?? '',
  });

  // Load saved clips from localStorage and keep in sync
  useEffect(() => {
    setSavedClips(getSavedClips());
    const unsub = onSavedClipsUpdated(() => setSavedClips(getSavedClips()));
    return unsub;
  }, []);

  // Not signed in state
  if (!authLoading && !user) {
    return (
      <div className="max-w-screen-lg mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <div
          className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
        >
          <LogIn size={32} style={{ color: 'var(--muted-foreground)' }} />
        </div>
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
          Not signed in yet.
        </h2>
        <p className="text-sm mb-6 text-center max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
          Sign in or create an account to view your profile, upload clips, and connect with the community.
        </p>
        <Link
          href="/sign-up-login-screen"
          className="btn-primary px-6 py-2.5 text-sm font-semibold rounded-xl flex items-center gap-2"
        >
          <LogIn size={15} />
          Sign In / Create Account
        </Link>
      </div>
    );
  }

  // Loading state
  if (authLoading) {
    return (
      <div className="max-w-screen-lg mx-auto px-4 py-20 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const profile = user!.profile!;
  const profileStats = [
    { key: 'views', label: 'Total Views', value: 0, icon: Eye, color: 'var(--accent)' },
    { key: 'downloads', label: 'Downloads', value: 0, icon: Download, color: '#22C55E' },
    { key: 'uploads', label: 'Uploads', value: profileClips.length, icon: Upload, color: 'var(--primary)' },
    { key: 'likes', label: 'Total Likes', value: 0, icon: Heart, color: '#EF4444' },
  ];

  function handleImageFile(file: File, type: 'avatar' | 'banner') {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (type === 'avatar') updateProfile({ avatarUrl: dataUrl });
      else updateProfile({ bannerUrl: dataUrl });
    };
    reader.readAsDataURL(file);
  }

  return (
    <div className="max-w-screen-lg mx-auto">
      {/* Banner */}
      <div className="relative w-full overflow-hidden" style={{ height: 200 }}>
        {profile.bannerUrl ? (
          <AppImage
            src={profile.bannerUrl}
            alt="Profile banner"
            fill
            className="object-cover"
            sizes="100vw"
            priority
          />
        ) : (
          <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, rgba(108,99,255,0.3) 0%, rgba(91,84,232,0.15) 100%)' }} />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,10,15,0.2) 0%, rgba(10,10,15,0.7) 100%)' }} />

        {/* Top-right actions */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {/* Change Banner button */}
          <button
            onClick={() => bannerInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150 hover:scale-105"
            style={{ background: 'rgba(10,10,15,0.75)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
            title="Change Banner"
          >
            <Camera size={13} />
            Banner
          </button>
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile(f, 'banner'); e.target.value = ''; }}
          />
          {/* Settings */}
          <Link
            href="/account"
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-150 hover:scale-105"
            style={{ background: 'rgba(10,10,15,0.7)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}
            title="Account Settings"
          >
            <Settings size={16} />
          </Link>
        </div>
      </div>

      {/* Avatar + Identity */}
      <div className="px-4 sm:px-6">
        <div className="flex items-end justify-between -mt-12 mb-4">
          {/* Avatar with change button */}
          <div className="relative flex-shrink-0">
            <div
              className="relative w-24 h-24 rounded-2xl overflow-hidden"
              style={{ border: '3px solid var(--background)', boxShadow: '0 8px 32px rgba(108,99,255,0.3)' }}
            >
              {profile.avatarUrl ? (
                <AppImage
                  src={profile.avatarUrl}
                  alt={`${profile.displayName} profile avatar`}
                  fill
                  className="object-cover"
                  sizes="96px"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--muted)', color: 'var(--primary)' }}>
                  {profile.displayName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            {/* Camera overlay */}
            <button
              onClick={() => avatarInputRef.current?.click()}
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 hover:scale-110"
              style={{ background: 'var(--primary)', border: '2px solid var(--background)', color: 'white' }}
              title="Change Profile Picture"
            >
              <Camera size={12} />
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile(f, 'avatar'); e.target.value = ''; }}
            />
          </div>

          {/* Share + Social icons */}
          <div className="flex items-center gap-2 pb-1">
            {SOCIAL_PLATFORMS.filter(
              (p) => !!((profile.socialLinks as Record<string, string> | undefined)?.[p.key] ?? '')
            ).map((platform) => {
              const url = (profile.socialLinks as Record<string, string>)[platform.key];
              return (
                <a
                  key={platform.key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-150 hover:scale-110"
                  style={{
                    background: `${platform.color}18`,
                    border: `1px solid ${platform.color}44`,
                    color: platform.color,
                  }}
                  title={platform.label}
                >
                  <Link2 size={15} />
                </a>
              );
            })}
            <button
              className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-150 hover:scale-105"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}
              title="Share Profile"
            >
              <ExternalLink size={15} />
            </button>
          </div>
        </div>

        {/* Name + Role Badge */}
        <div className="mb-2">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h1 className="text-xl font-bold" style={{ color: 'var(--foreground)', letterSpacing: '-0.02em' }}>
              {profile.displayName}
            </h1>
            {isOwner ? (
              <Crown size={16} style={{ color: '#f59e0b', flexShrink: 0 }} fill="currentColor" />
            ) : profile.role === 'creator' ? (
              <Star size={15} style={{ color: 'var(--premium)', flexShrink: 0 }} fill="currentColor" />
            ) : null}
          </div>
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>@{profile.username}</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {isOwner ? (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1"
                style={{
                  background: 'linear-gradient(135deg, rgba(245,158,11,0.2) 0%, rgba(251,191,36,0.15) 100%)',
                  color: '#f59e0b',
                  border: '1px solid rgba(245,158,11,0.4)',
                }}
              >
                <Crown size={9} fill="currentColor" />
                Owner
              </span>
            ) : (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-md"
                style={{
                  background: profile.role === 'creator' ? 'rgba(245,158,11,0.12)' : 'rgba(59,158,255,0.12)',
                  color: profile.role === 'creator' ? 'var(--premium)' : 'var(--verified)',
                  border: `1px solid ${profile.role === 'creator' ? 'rgba(245,158,11,0.25)' : 'rgba(59,158,255,0.25)'}`,
                }}
              >
                {profile.role === 'creator' ? 'Creator' : 'Editor'}
              </span>
            )}
          </div>
        </div>

        {/* Bio */}
        {profile.bio && (
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--secondary-foreground)', maxWidth: 520 }}>
            {profile.bio}
          </p>
        )}

        {/* Follower counts */}
        <div className="flex items-center gap-5 mb-5">
          <div className="flex items-center gap-1.5">
            <span className="text-base font-bold" style={{ color: 'var(--foreground)' }}>0</span>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Followers</span>
          </div>
          <div className="w-px h-4" style={{ background: 'var(--border)' }} />
          <div className="flex items-center gap-1.5">
            <span className="text-base font-bold" style={{ color: 'var(--foreground)' }}>0</span>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Following</span>
          </div>
          <div className="w-px h-4" style={{ background: 'var(--border)' }} />
          <div className="flex items-center gap-1.5">
            <span className="text-base font-bold" style={{ color: 'var(--foreground)' }}>{loadingClips ? '—' : profileClips.length}</span>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Clips</span>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {profileStats.map((stat) => {
            const StatIcon = stat.icon;
            return (
              <div key={stat.key} className="rounded-xl p-3 flex flex-col gap-1.5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}18` }}>
                  <StatIcon size={15} style={{ color: stat.color }} />
                </div>
                <span className="text-lg font-bold tabular-nums" style={{ color: 'var(--foreground)', letterSpacing: '-0.02em' }}>
                  {formatCount(stat.value)}
                </span>
                <span className="text-[11px] font-medium" style={{ color: 'var(--muted-foreground)' }}>
                  {stat.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 p-1 rounded-xl w-fit" style={{ background: 'var(--muted)' }}>
          {[
            { key: 'clips', label: 'Clips', icon: Grid3X3 },
            { key: 'saved', label: 'Saved', icon: Bookmark },
          ].map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as 'clips' | 'saved')}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
                style={{
                  background: isActive ? 'var(--card)' : 'transparent',
                  color: isActive ? 'var(--foreground)' : 'var(--muted-foreground)',
                  boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
                }}
              >
                <TabIcon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Clips Grid */}
        {activeTab === 'clips' && (
          <>
            {loadingClips ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="rounded-2xl animate-pulse" style={{ background: 'var(--muted)', height: 220 }} />
                ))}
              </div>
            ) : emptyClips ? (
              <div className="flex flex-col items-center justify-center py-16 rounded-2xl mb-4" style={{ background: 'var(--card)', border: '1px dashed var(--border)' }}>
                <Grid3X3 size={36} style={{ color: 'var(--muted-foreground)', opacity: 0.4, marginBottom: 12 }} />
                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>No clips uploaded yet</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Upload your first clip to get started!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                {profileClips.map((clip) => (
                  <div key={clip.id} className="relative group/clip">
                    <ClipCard clip={clip} />
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this clip? This cannot be undone.')) {
                          deleteClip(clip.id);
                        }
                      }}
                      className="absolute top-2 right-2 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 hover:scale-110 z-10"
                      style={{
                        background: 'rgba(239,68,68,0.85)',
                        border: '1px solid rgba(239,68,68,0.5)',
                        color: 'white',
                        backdropFilter: 'blur(4px)',
                      }}
                      title="Delete clip"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'saved' && (
          <div className="pb-4">
            {savedClips.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 rounded-2xl mb-4" style={{ background: 'var(--card)', border: '1px dashed var(--border)' }}>
                <Bookmark size={36} style={{ color: 'var(--muted-foreground)', opacity: 0.4, marginBottom: 12 }} />
                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>No saved clips yet</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Tap the bookmark icon on any clip to save it here.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedClips.map((clip) => (
                  <ClipCard key={clip.id} clip={clip} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Social Media Links Tab */}
      </div>
    </div>
  );
}